/* PaywallSheet — global modal that converts every backend HTTP 402 into a
 * conversion-friendly upgrade prompt instead of a generic red toast.
 *
 * Wiring (see `PaywallProvider` and the axios interceptor in `lib/api.js`):
 *   1. Any API call → backend raises 402 with structured `detail`.
 *   2. Interceptor reads `detail.code` + `detail.message` and fires a
 *      `paywall:show` window event with the full payload.
 *   3. <PaywallProvider> listens and mounts this sheet with the right copy
 *      mapped from `detail.code`. The original axios error still rejects
 *      so the calling component can also drop its own loading UI.
 *
 * Why a single global sheet (vs. inline cards on every page)?
 *   - One canonical look = trust + recognition for the lawyer.
 *   - New paywalled feature can be added in the backend WITHOUT touching
 *     any frontend page — the interceptor catches it automatically.
 *   - Bottom-sheet pattern on mobile keeps context (the page behind is
 *     visible blurred) so the lawyer doesn't lose orientation.
 */
import { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X as XIcon,
  Gavel, ChatCircleText, FilePlus, Brain, Crown,
  ChatsCircle, Phone, ListBullets, UsersThree,
} from "@phosphor-icons/react";
import PaywallCard from "./PaywallCard";

/* Map structured 402 code → presentation. We deliberately localise the
   strings here (not the backend) so the UI team can iterate on copy
   without redeploying the API. The backend's `detail.message` is shown
   below the title as the authoritative reason, so quota numbers etc. are
   always accurate even if this map drifts. */
const PRESENTATION = {
  cassation_locked: {
    icon: Gavel,
    title: "مَكتَبَة قَرارات التَّمييز مَحميَّة بِخطّة الاشتراك",
    subtitle: "خطّتك الحاليّة لا تَشمَل الوُصول لِقَرارات مَحكَمَة التَّمييز الاتّحاديّة.",
    highlights: [
      "بَحث فَوريّ في النَّصّ الكامِل لِكُلّ القَرارات",
      "تَصنيف حَسَب نَوع القَضيّة وَتاريخ الإصدار",
      "اِستِشهاد ذَكيّ داخِل تَحليل قَضاياك",
      "تَحديث أُسبوعيّ بِأَحدَث المَبادِئ",
    ],
  },
  cassation_quota_exceeded: {
    icon: Gavel,
    title: "اِستَنفَدت حِصَّتَك الشَّهريَّة مِن قَرارات التَّمييز",
    subtitle: "اِنتَهَت قَرارات هذا الشَّهر في باقَتِك الحاليّة. تَجَدَّد حِصَّتُك مَطلَع كُلّ شَهر، أَو ارقَ لِباقَة «النُّخبَة» لِلوُصول غَير المَحدود.",
    highlights: [
      "قِراءَة غَير مَحدودَة لِكُلّ قَرار",
      "تَحميل النُّصوص الكامِلَة بِصيغَة PDF",
      "حِفظ القَرارات في مَكتَبَتِك الشَّخصيَّة",
    ],
  },
  quota_exceeded: {
    icon: Brain,
    title: "اِستَنفَدت حِصَّتَك الشَّهريَّة مِن هذه المَيزَة",
    subtitle: "كُلّ باقَة لَدَيها حِصَّة شَهريَّة مُجَدَّدَة تِلقائياً. ارقَ لِباقَة أَعلى لِزيادَة الحِصَّة أَو اِنتَظِر تَجَدُّدها مَطلَع الشَّهر القادِم.",
    highlights: [
      "حِصَص أَكبَر لِلتَّحليل وَالصِّياغَة وَالمُحادَثَة",
      "أَولَويَّة في تَنفيذ طَلَبات الذَّكاء الاصطِناعيّ",
      "إِشعار مُبَكِّر عِندَ اِقتِراب نَفاد الحِصَّة",
    ],
  },
  feature_locked: {
    icon: Crown,
    title: "هذه المَيزَة غَير مُتاحَة في باقَتِك",
    subtitle: "ارقَ لِخطّة أَعلى لِتَفعيل هذه المَيزَة.",
    highlights: [],
  },
  resource_limit_exceeded: {
    icon: ListBullets,
    title: "وَصَلت إلى الحَدّ الأَقصى في باقَتِك",
    subtitle: "كُلّ باقَة تُتيح عَدَداً مُحَدَّداً مِن القَضايا وَالعُملاء. ارقَ لِباقَة أَعلى لِتَوسيع الحَدّ.",
    highlights: [
      "إِدارَة عَدَد أَكبَر مِن المَلَفّات النَّشِطَة",
      "حِفظ غَير مَحدود لِأَرشيف القَضايا المُغلَقَة",
    ],
  },
};

/* Specific feature_flag → icon override (so calls/messaging get phone/chat icons). */
const FEATURE_ICONS = {
  calls_enabled: Phone,
  messaging_enabled: ChatsCircle,
  directory_visible: UsersThree,
  chat: ChatCircleText,
  draft: FilePlus,
  case_analyze: Brain,
};

export default function PaywallSheet({ payload, onClose }) {
  // Close on Escape key.
  useEffect(() => {
    if (!payload) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [payload, onClose]);

  if (!payload) return null;

  const code = payload.code || "feature_locked";
  const preset = PRESENTATION[code] || PRESENTATION.feature_locked;
  // Per-feature icon override (e.g. calls → phone).
  const featureKey = payload.feature || payload.feature_flag || "";
  const Icon = FEATURE_ICONS[featureKey] || preset.icon;

  // Authoritative reason from backend — wins over our hard-coded subtitle
  // when present, because it contains real numbers (e.g. "5/5 رسائل").
  const subtitle = payload.message || preset.subtitle;

  return createPortal(
    <div
      className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="paywall-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md relative animate-paywall-up"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="إغلاق"
          data-testid="paywall-sheet-close"
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur flex items-center justify-center transition"
        >
          <XIcon size={16} weight="bold" className="text-white" />
        </button>
        <PaywallCard
          icon={Icon}
          title={preset.title}
          subtitle={subtitle}
          highlights={preset.highlights}
          ctaLabel="اِطَّلِع على الخُطَط"
          dataTestId="paywall-sheet-card"
        />
      </div>
      <style>{`
        @keyframes paywall-up {
          from { transform: translateY(24px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .animate-paywall-up { animation: paywall-up 240ms cubic-bezier(0.22,1,0.36,1); }
      `}</style>
    </div>,
    document.body,
  );
}
